import { Callback, UserTraits } from '@segment/analytics-next'
import { Environment, getRealEnvironment } from '../env'

interface GtagEventParams {
  readonly name: string;
  readonly label: string;
  readonly category: string;
  readonly value?: number;
  readonly nonInteraction?: boolean;
  readonly dimensions?: Record<string, string | number>
}

export interface AnalyticsProduct {
	readonly brand: string;
	readonly category: string;
	readonly currency: string;
	readonly image_url?: string;
	readonly name: string;
	readonly price: number;
	readonly product_id: string;
	readonly quantity: number;
	readonly value?: number;
	readonly variant?: string;
	readonly coupon?: string;
	readonly position?: number;
	readonly sku?: string;
	readonly url?: string;
}

export interface AnalyticsOrderDetails {
	readonly subtotal: number;
	readonly currency: string;
	readonly checkout_id?: string;
	readonly order_id?: string;
	readonly affiliation?: string;
	readonly total?: number;
	readonly revenue?: number;
	readonly shipping?: number;
	readonly discount?: number;
	readonly coupon?: string;
}

/**
 * https://developers.google.com/analytics/devguides/collection/gtagjs/events
 *
 * gtag('event', 'video_auto_play_start', {
 * 	'event_label': 'My promotional video',
 * 	'event_category': 'video_auto_play',
 * 	'non_interaction': true
 * });
 */
export function sendGtagEvent({
	name,
	label,
	category,
	value,
	dimensions = {},
	nonInteraction = false,
} : GtagEventParams) {
	window.gtag?.('event', name, {
		value,
		event_label: label,
		event_category: category,
		non_interaction: nonInteraction,
		...dimensions,
	})
}

function debugEvent(event: string, properties?: unknown, context?: unknown) : void {
	const realEnvironment = getRealEnvironment()

	if (realEnvironment === Environment.Production) {
		return
	}

	// eslint-disable-next-line no-console
	console.info(
		`%cSegment Event%c ${event} %o %o`,
		'color: #5a2; font-weight: 700; border: 1px solid #5a2; padding: 2px 5px; border-radius: 5px;',
		'color: #000; font-weight: 700;',
		properties,
		context,
	)
}

interface StepCompletedPayload {
	readonly reservation_id: string;
	readonly step_name: string;
	readonly phone?: string;
	readonly type_of_trip?: string[];
	readonly first_name?: string;
	readonly last_name?: string;
	readonly citizenship?: string;
	readonly date_of_birth?: string;
	readonly country_of_residence?: string;
	readonly address?: string;
	readonly document_type?: string;
	readonly email_optin?: string;
}

interface GroupTraits {
	readonly adult_count: number;
	readonly child_count: number;
	readonly confirmation_number: string;
	readonly end_date: string;
	readonly group: 'reservation';
	readonly start_date: string;
	readonly stay_duration: number;
}

async function getIsAnalyticsAllowed(): Promise<boolean> {
	try {
		// eslint-disable-next-line no-underscore-dangle
		const details = await window.__ucCmp.getConsentDetails()

		// eslint-disable-next-line no-restricted-syntax
		for (const category of Object.values(details.categories)) {
			if (category.name === 'Analytics') {
				return category.state === 'ALL_ACCEPTED'
			}
		}

		return false
	} catch (error) {
		return false
	}
}

async function getContext() {
	const isAnalyticsAllowed = await getIsAnalyticsAllowed()

	return {
		consent: {
			categoryPreferences: {
				analytics: isAnalyticsAllowed,
			},
		},
	}
}

interface TrackEventProperties {
	readonly reservation_id: string;
	readonly [key: string]: unknown;
}

async function sendTrackEvent(eventName: string, properties: TrackEventProperties) : Promise<void> {
	const context = await getContext()

	debugEvent(`Track: ${eventName}`, properties, context)

	window.analytics?.track(eventName, {
		...properties,
		is_logged_in: false,
		app: 'Check-in',
	}, context)
}

export async function sendPageViewEvent(pageName: string): Promise<void> {
	const properties = {
		is_logged_in: false,
		app: 'Check-in',
	}

	const context = await getContext()

	debugEvent('Page', properties, context)

	window.analytics?.page(pageName, properties, context)
}

export async function sendIdentifyEvent(userId: string, traits: UserTraits, callback?: Callback): Promise<void> {
	const context = await getContext()

	debugEvent('Identify', { userId, traits }, context)

	window.analytics?.identify(userId, traits, context, callback)
}

export async function setTrackingGroup(groupId: string, traits: GroupTraits): Promise<void> {
	const context = await getContext()

	debugEvent('Group', { groupId, traits }, context)

	window.analytics?.group(groupId, traits, context)
}

export function sendStepCompletedEvent(payload: StepCompletedPayload) {
	sendTrackEvent('Check-in Step Completed', { ...payload })
}

export function sendCheckInStartedEvent(reservationId: string) {
	sendTrackEvent('Check-in Started', {
		reservation_id: reservationId,
	})
}

export function sendCheckInCompletedEvent(payload: TrackEventProperties) {
	sendTrackEvent('Check-in Completed', payload)
}

export function sendArrivalTimeSelectedEvent(
	reservationId: string,
	time: string,
	price: number,
) {
	sendTrackEvent('Arrival Time Selected', {
		reservation_id: reservationId,
		arrival_time: time,
		additional_cost: price,
	})
}

export function sendDepartureTimeSelectedEvent(
	reservationId: string,
	time: string,
	price: number,
) {
	sendTrackEvent('Departure Time Selected', {
		reservation_id: reservationId,
		departure_time: time,
		additional_cost: price,
	})
}

// https://segment.com/docs/connections/spec/ecommerce/v2/#product-list-viewed
export function sendProductListViewedEvent(
	reservationId: string | null,
	category: string,
	products: AnalyticsProduct[],
) {
	if (reservationId === null) {
		return
	}

	sendTrackEvent('Product List Viewed', {
		reservation_id: reservationId,
		category,
		products,
	})
}

// https://segment.com/docs/connections/spec/ecommerce/v2/#product-viewed
export function sendProductViewedEvent(reservationId: string, product: AnalyticsProduct) {
	sendTrackEvent('Product Viewed', {
		reservation_id: reservationId,
		...product,
	})
}

// https://segment.com/docs/connections/spec/ecommerce/v2/#product-added
export function sendProductAddedEvent(reservationId: string | null, product: AnalyticsProduct) {
	if (reservationId === null) {
		return
	}

	sendTrackEvent('Product Added', {
		reservation_id: reservationId,
		...product,
	})
}

// https://segment.com/docs/connections/spec/ecommerce/v2/#product-removed
export function sendProductRemovedEvent(reservationId: string | null, product: AnalyticsProduct) {
	if (reservationId === null) {
		return
	}

	sendTrackEvent('Product Removed', {
		reservation_id: reservationId,
		...product,
	})
}

// https://segment.com/docs/connections/spec/ecommerce/v2/#checkout-step-viewed
export function sendCheckoutStepViewedEvent(reservationId: string) {
	sendTrackEvent('Checkout Step Viewed', {
		reservation_id: reservationId,
	})
}

// https://segment.com/docs/connections/spec/ecommerce/v2/#order-completed
export function sendOrderCompletedEvent(
	reservationId: string,
	orderDetails: AnalyticsOrderDetails,
	products: AnalyticsProduct[],
) {
	sendTrackEvent('Order Completed', {
		reservation_id: reservationId,
		...orderDetails,
		products,
	})
}
